import { useMemo } from "react";
import { useConfigLookup } from "@/hooks/useConfigLookup";
import type { KpiVariant } from "@/components/shared/CompactKpiRibbon";

export const CANONICAL_MOLD_TYPE_ORDER = [
  "DIE_CUT",
  "FOIL_STAMP",
  "EMBOSS_DIE",
  "DEBOSS_DIE",
  "SPOT_UV",
  "SILK_SCREEN",
] as const;

export type CanonicalMoldTypeCode = (typeof CANONICAL_MOLD_TYPE_ORDER)[number];

export interface MoldTypeVisualMeta {
  canonicalCode: string;
  prefix: string;
  shortLabel: string;
  fullLabel: string;
  badgeClass: string;
  kpiVariant: KpiVariant;
}

export interface MoldTypeTaxonomyItem extends MoldTypeVisualMeta {
  pillLabel: string;
}

const MOLD_TYPE_ALIASES: Record<string, string> = {
  DIE_CUT: "DIE_CUT",
  KB: "DIE_CUT",
  FOIL_STAMP: "FOIL_STAMP",
  FOIL: "FOIL_STAMP",
  EN: "FOIL_STAMP",
  KN: "FOIL_STAMP",
  EMBOSS_DIE: "EMBOSS_DIE",
  EMBOSS: "EMBOSS_DIE",
  BN: "EMBOSS_DIE",
  KD: "EMBOSS_DIE",
  DEBOSS_DIE: "DEBOSS_DIE",
  DEBOSS: "DEBOSS_DIE",
  BC: "DEBOSS_DIE",
  SPOT_UV: "SPOT_UV",
  UV: "SPOT_UV",
  PB: "SPOT_UV",
  SILK_SCREEN: "SILK_SCREEN",
  KL: "SILK_SCREEN",
  PLATE: "PLATE",
  CTP: "PLATE",
};

const MOLD_TYPE_DEFAULTS: Record<string, MoldTypeVisualMeta> = {
  DIE_CUT: {
    canonicalCode: "DIE_CUT",
    prefix: "KB",
    shortLabel: "Khuôn bế",
    fullLabel: "Khuôn bế đứt (Die-cut)",
    badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
    kpiVariant: "blue",
  },
  FOIL_STAMP: {
    canonicalCode: "FOIL_STAMP",
    prefix: "EN",
    shortLabel: "Ép kim / ép nhũ",
    fullLabel: "Khuôn ép kim / ép nhũ (Foil)",
    badgeClass: "bg-amber-50 text-amber-800 border-amber-200",
    kpiVariant: "amber",
  },
  EMBOSS_DIE: {
    canonicalCode: "EMBOSS_DIE",
    prefix: "BN",
    shortLabel: "Khuôn thúc nổi",
    fullLabel: "Khuôn thúc nổi (Emboss)",
    badgeClass: "bg-purple-50 text-purple-700 border-purple-200",
    kpiVariant: "purple",
  },
  DEBOSS_DIE: {
    canonicalCode: "DEBOSS_DIE",
    prefix: "BC",
    shortLabel: "Khuôn dập chìm",
    fullLabel: "Khuôn dập chìm (Deboss)",
    badgeClass: "bg-indigo-50 text-indigo-700 border-indigo-200",
    kpiVariant: "indigo",
  },
  SPOT_UV: {
    canonicalCode: "SPOT_UV",
    prefix: "UV",
    shortLabel: "UV định hình",
    fullLabel: "Lưới / khuôn phủ UV định hình (Spot UV)",
    badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    kpiVariant: "emerald",
  },
  SILK_SCREEN: {
    canonicalCode: "SILK_SCREEN",
    prefix: "KL",
    shortLabel: "Lưới lụa",
    fullLabel: "Lưới in lụa (Silk Screen)",
    badgeClass: "bg-slate-100 text-slate-700 border-slate-300",
    kpiVariant: "slate",
  },
};

export function normalizeMoldTypeCode(raw?: string | null): string {
  if (!raw) return "DIE_CUT";
  const upper = String(raw).trim().toUpperCase();
  if (!upper) return "DIE_CUT";
  return MOLD_TYPE_ALIASES[upper] || upper;
}

export function isMoldTypeExcluded(raw?: string | null): boolean {
  if (!raw) return false;
  return normalizeMoldTypeCode(raw) === "PLATE";
}

export function formatShortMoldTypeLabel(rawName?: string | null, code?: string | null): string {
  const canonical = normalizeMoldTypeCode(code);
  const raw = (rawName || "").trim();
  if (!raw) {
    return MOLD_TYPE_DEFAULTS[canonical]?.shortLabel || canonical;
  }

  // Gỡ hậu tố tiếng Anh trong ngoặc đơn ở cuối (VD: "(Die-cut)", "(Spot UV)")
  const withoutParens = raw.replace(/\s*\([^)]*\)\s*$/g, "").trim();
  const lower = withoutParens.toLowerCase();

  if (lower === "khuôn bế đứt") return "Khuôn bế";
  if (lower === "khuôn ép kim / ép nhũ" || lower === "khuôn ép nhũ / ép kim") {
    return "Ép kim / ép nhũ";
  }
  if (lower === "lưới / khuôn phủ uv định hình" || lower === "khuôn / lưới phủ uv định hình") {
    return "UV định hình";
  }
  if (lower === "lưới in lụa" || lower === "lưới kéo lụa") {
    return "Lưới lụa";
  }

  return withoutParens || MOLD_TYPE_DEFAULTS[canonical]?.shortLabel || canonical;
}

export function getMoldTypeVisualMeta(
  code?: string | null,
  dynamicFullName?: string | null,
): MoldTypeVisualMeta {
  const canonical = normalizeMoldTypeCode(code);
  const preset = MOLD_TYPE_DEFAULTS[canonical];
  if (preset) {
    const fullLabel = (dynamicFullName || "").trim() || preset.fullLabel;
    const shortLabel = dynamicFullName
      ? formatShortMoldTypeLabel(dynamicFullName, canonical)
      : preset.shortLabel;
    return {
      ...preset,
      fullLabel,
      shortLabel,
    };
  }

  const fallbackFull = (dynamicFullName || code || "Khuôn gia công").trim();
  const fallbackPrefix = canonical.replace(/[^A-Z0-9]/g, "").slice(0, 2) || "KH";
  return {
    canonicalCode: canonical,
    prefix: fallbackPrefix,
    shortLabel: formatShortMoldTypeLabel(fallbackFull, canonical),
    fullLabel: fallbackFull,
    badgeClass: "bg-slate-50 text-slate-700 border-slate-200",
    kpiVariant: "default",
  };
}

export function buildMoldTypeTaxonomyItems(
  rawOptions: Array<{ id: string; label: string }>,
): MoldTypeTaxonomyItem[] {
  const source =
    rawOptions.length > 0
      ? rawOptions
      : CANONICAL_MOLD_TYPE_ORDER.map((code) => ({
          id: code,
          label: MOLD_TYPE_DEFAULTS[code].fullLabel,
        }));

  const seen = new Set<string>();
  const items: MoldTypeTaxonomyItem[] = [];

  for (const opt of source) {
    if (!opt?.id || isMoldTypeExcluded(opt.id)) continue;
    const canonical = normalizeMoldTypeCode(opt.id);
    if (canonical === "OTHER" || seen.has(canonical)) continue;
    seen.add(canonical);
    const meta = getMoldTypeVisualMeta(canonical, opt.label);
    items.push({
      ...meta,
      pillLabel: `${meta.prefix} • ${meta.shortLabel}`,
    });
  }

  const orderIndex = new Map<string, number>(
    CANONICAL_MOLD_TYPE_ORDER.map((code, idx) => [code, idx]),
  );

  items.sort((a, b) => {
    const idxA = orderIndex.get(a.canonicalCode);
    const idxB = orderIndex.get(b.canonicalCode);
    if (idxA !== undefined && idxB !== undefined) return idxA - idxB;
    if (idxA !== undefined) return -1;
    if (idxB !== undefined) return 1;
    return a.shortLabel.localeCompare(b.shortLabel, "vi");
  });

  return items;
}

export function useMoldTypeTaxonomy() {
  const { options, isLoading } = useConfigLookup("mold_types");

  const taxonomyItems = useMemo(
    () => buildMoldTypeTaxonomyItems(options),
    [options],
  );

  const taxonomyByCode = useMemo(() => {
    const map = new Map<string, MoldTypeTaxonomyItem>();
    for (const item of taxonomyItems) {
      map.set(item.canonicalCode, item);
    }
    return map;
  }, [taxonomyItems]);

  return {
    taxonomyItems,
    taxonomyByCode,
    isLoading,
  };
}
